<template>
  <div class="role-permissions" :aria-label="ariaLabel">
    <section v-for="group in groups" :key="group.key" class="role-permissions__group">
      <div class="role-permissions__group-header">
        <div>
          <h3>{{ group.label }}</h3>
          <p>{{ group.description }}</p>
        </div>
        <q-checkbox
          :model-value="isGroupChecked(group)"
          :indeterminate="isGroupIndeterminate(group)"
          :disable="disabled"
          :label="`Выбрать группу «${group.label}»`"
          @update:model-value="(checked) => setGroup(group, checked)"
        />
      </div>

      <q-list separator class="role-permissions__list">
        <q-item v-for="permission in group.permissions" :key="permission.key" tag="label">
          <q-item-section avatar top>
            <q-checkbox
              :model-value="normalizedValue[permission.key]"
              :disable="disabled || isRequiredByEdit(permission.key)"
              :data-testid="testId(permission.key)"
              :aria-label="permission.label"
              @update:model-value="(checked) => setPermission(permission.key, checked)"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ permission.label }}</q-item-label>
            <q-item-label caption>{{ permission.description }}</q-item-label>
            <q-item-label v-if="isRequiredByEdit(permission.key)" caption class="text-primary">
              Включено автоматически, потому что разрешено редактирование.
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </section>

    <p class="role-permissions__preview">Права: {{ permissionsPreview }}.</p>
  </div>
</template>

<script>
export const ROLE_PERMISSION_KEYS = Object.freeze([
  'edit_org',
  'change_billing',
  'open_fin_documents',
  'edit_fin_documents',
  'open_stock',
  'edit_stock'
])

const GROUPS = Object.freeze([
  {
    key: 'management',
    label: 'Управление',
    description: 'Организация, сотрудники, тарифы и платежные документы.',
    permissions: [
      { key: 'edit_org', label: 'Управление организацией', description: 'Редактирование структуры, ролей и приглашений.' },
      { key: 'change_billing', label: 'Тарифы и счета', description: 'Изменение тарифного плана и платежных настроек.' }
    ]
  },
  {
    key: 'accounting',
    label: 'Бухгалтерия',
    description: 'Доступ к документам, счетам и отчетности.',
    permissions: [
      { key: 'open_fin_documents', label: 'Просмотр бухгалтерии', description: 'Просмотр документов, счетов и отчетов без права изменения.' },
      { key: 'edit_fin_documents', label: 'Редактирование бухгалтерии', description: 'Создание и изменение бухгалтерских документов.' }
    ]
  },
  {
    key: 'stock',
    label: 'Склад',
    description: 'Остатки, движения, номенклатура и складские операции.',
    permissions: [
      { key: 'open_stock', label: 'Просмотр склада', description: 'Просмотр остатков, движений, отчетов и номенклатуры.' },
      { key: 'edit_stock', label: 'Редактирование склада', description: 'Изменение складских справочников, настроек и операций.' }
    ]
  }
])

export function normalizeRolePermissions(value = {}) {
  const normalized = Object.fromEntries(ROLE_PERMISSION_KEYS.map((key) => [key, value[key] === true]))
  if (normalized.edit_fin_documents) normalized.open_fin_documents = true
  if (normalized.edit_stock) normalized.open_stock = true
  return normalized
}

export default {
  name: 'RolePermissionsEditor',
  emits: ['update:modelValue'],
  props: {
    modelValue: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    testIdPrefix: { type: String, default: 'role' },
    ariaLabel: { type: String, default: 'Права доступа рабочего места' }
  },
  computed: {
    groups() { return GROUPS },
    normalizedValue() { return normalizeRolePermissions(this.modelValue) },
    permissionsPreview() {
      const labels = GROUPS.flatMap((group) => group.permissions)
        .filter((permission) => this.normalizedValue[permission.key])
        .map((permission) => permission.label.toLowerCase())
      return labels.length ? labels.join(', ') : 'без дополнительных прав'
    }
  },
  methods: {
    testId(key) { return `${this.testIdPrefix}-${key.replaceAll('_', '-')}` },
    emitValue(value) { this.$emit('update:modelValue', normalizeRolePermissions(value)) },
    setPermission(key, checked) {
      const next = { ...this.normalizedValue, [key]: checked === true }
      if (key === 'edit_fin_documents' && checked) next.open_fin_documents = true
      if (key === 'edit_stock' && checked) next.open_stock = true
      this.emitValue(next)
    },
    setGroup(group, checked) {
      const next = { ...this.normalizedValue }
      group.permissions.forEach((permission) => { next[permission.key] = checked === true })
      this.emitValue(next)
    },
    isGroupChecked(group) { return group.permissions.every((permission) => this.normalizedValue[permission.key]) },
    isGroupIndeterminate(group) {
      const count = group.permissions.filter((permission) => this.normalizedValue[permission.key]).length
      return count > 0 && count < group.permissions.length
    },
    isRequiredByEdit(key) {
      return (key === 'open_fin_documents' && this.normalizedValue.edit_fin_documents) ||
        (key === 'open_stock' && this.normalizedValue.edit_stock)
    }
  }
}
</script>

<style scoped>
.role-permissions { max-width: 52rem; }
.role-permissions__group + .role-permissions__group { margin-top: var(--sid-space-5); }
.role-permissions__group-header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--sid-space-4); }
.role-permissions__group-header h3, .role-permissions__group-header p { margin: 0; }
.role-permissions__group-header p, .role-permissions__preview { color: var(--sid-color-text-secondary); }
.role-permissions__list { margin-top: var(--sid-space-2); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-large); overflow: hidden; }
.role-permissions__preview { margin: var(--sid-space-4) 0 0; }
@media (max-width: 599px) {
  .role-permissions__group-header { flex-direction: column; }
}
</style>
